// SETTINGS V2 — Služba (Service Settings + Business Hours) page — SETTINGS_V2_SP8.
// Replaces the SP0 ComingSoonGhostV2 for /settings-v2/service. Reuses the
// serviceProviderPricing slice/API/DTO 100% UNCHANGED and ports the legacy
// SettingsService binding logic VERBATIM (seed / dirty / save coercions / the
// full 30-key payload).
//
// COORDINATED PAIR WITH SP7 (Pricing): same slice, same endpoint, same DB row,
// WHOLE-ROW-overwrite PUT. This save echoes the ~18 Pricing-owned passthrough
// keys from `settings` (hourly/travel rates, the 6 period-code/custom keys, the
// 6 include* booleans) plus baseLatitude/baseLongitude — omit any and the
// Pricing page's stored values get WIPED. This reciprocal echo mirrors SP7's
// business-hours echo. No post-save refetch (PM ruling, §2): the mount-refetch
// bounds the staleness across page hops.
import React, { useEffect, useMemo, useState } from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import ActionToast from "../../../../shared/components/ActionToast";
import {
  getPricingDefaults,
  savePricingDefaults,
  clearPricingSuccess,
  clearPricingError,
} from "../../serviceProviderPricingSlice";
import { SCard, SInputField, SSaveBtn, SComingPill } from "../atoms";
import ServiceBusinessHoursCardV2 from "./ServiceBusinessHoursCardV2";
import { formatTimeSpan, toTimeSpan } from "./serviceV2TimeUtils";

const cardAnim = (i: number): React.CSSProperties => ({
  animation: `sv2-card-reveal 0.4s ease-out ${(i * 0.06).toFixed(2)}s both`,
});

const ServicePageV2: React.FC = () => {
  const dispatch = useAppDispatch();
  const { translate: t } = useTranslation("settings");
  const { translate: tCommon } = useTranslation("common");
  const { isPhone } = useBreakpoint();

  // `loading` deliberately not consumed — parity (§2: no skeleton).
  const { settings, saving, success, error } = useAppSelector(
    (s) => s.serviceProviderPricing
  );

  // ── Local state — Service Radius (legacy lines 27-28) ──
  const [radius, setRadius] = useState("");
  const [loadedRadius, setLoadedRadius] = useState("");

  // ── Local state — Business Hours (legacy lines 31-48) ──
  const [deadlineEnabled, setDeadlineEnabled] = useState(false);
  const [deadlineTime, setDeadlineTime] = useState("16:00");
  const [usePerDay, setUsePerDay] = useState(false);
  const [perDayTimes, setPerDayTimes] = useState<Record<string, string>>({
    monday: "16:00",
    tuesday: "16:00",
    wednesday: "16:00",
    thursday: "16:00",
    friday: "16:00",
    saturday: "",
    sunday: "",
  });
  const [loadedDeadlineEnabled, setLoadedDeadlineEnabled] = useState(false);
  const [loadedDeadlineTime, setLoadedDeadlineTime] = useState("16:00");
  const [loadedUsePerDay, setLoadedUsePerDay] = useState(false);
  const [loadedPerDayTimes, setLoadedPerDayTimes] = useState<Record<string, string>>({});

  // Auto-detected timezone (legacy lines 51-57 — memoized ONCE, deps []; the
  // PERSISTED orderDeadlineTimezoneId is never displayed, only this is).
  const detectedTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return "UTC";
    }
  }, []);

  // Mount: fetch settings (parity — refreshes the shared snapshot on entry so
  // the Pricing-owned passthrough echo stays fresh after the other page saved).
  useEffect(() => {
    dispatch(getPricingDefaults());
  }, [dispatch]);

  // Populate form when settings arrive (legacy lines 78-109 verbatim coercions).
  useEffect(() => {
    if (settings) {
      const r = settings.serviceRadiusKm != null ? String(settings.serviceRadiusKm) : "150";
      setRadius(r);
      setLoadedRadius(r);

      const de = settings.orderDeadlineEnabled ?? false;
      setDeadlineEnabled(de);
      setLoadedDeadlineEnabled(de);

      const dtime = formatTimeSpan(settings.orderDeadlineTime) || "16:00";
      setDeadlineTime(dtime);
      setLoadedDeadlineTime(dtime);

      const upd = settings.usePerDayDeadlines ?? false;
      setUsePerDay(upd);
      setLoadedUsePerDay(upd);

      const pdt: Record<string, string> = {
        monday: formatTimeSpan(settings.mondayDeadlineTime) || "16:00",
        tuesday: formatTimeSpan(settings.tuesdayDeadlineTime) || "16:00",
        wednesday: formatTimeSpan(settings.wednesdayDeadlineTime) || "16:00",
        thursday: formatTimeSpan(settings.thursdayDeadlineTime) || "16:00",
        friday: formatTimeSpan(settings.fridayDeadlineTime) || "16:00",
        saturday: formatTimeSpan(settings.saturdayDeadlineTime) || "",
        sunday: formatTimeSpan(settings.sundayDeadlineTime) || "",
      };
      setPerDayTimes(pdt);
      setLoadedPerDayTimes({ ...pdt });
    }
  }, [settings]);

  // Dirty detection (legacy lines 111-124 verbatim — with the deadline disabled,
  // edits to the hidden cutoff/per-day state deliberately do NOT count as dirty).
  const isDirty = useMemo(() => {
    if (loadedRadius !== "" && radius !== loadedRadius) return true;
    if (deadlineEnabled !== loadedDeadlineEnabled) return true;
    if (deadlineEnabled) {
      if (deadlineTime !== loadedDeadlineTime) return true;
      if (usePerDay !== loadedUsePerDay) return true;
      if (usePerDay && JSON.stringify(perDayTimes) !== JSON.stringify(loadedPerDayTimes)) return true;
    }
    return false;
  }, [
    radius, loadedRadius, deadlineEnabled, loadedDeadlineEnabled,
    deadlineTime, loadedDeadlineTime, usePerDay, loadedUsePerDay,
    perDayTimes, loadedPerDayTimes,
  ]);

  const handleSave = () => {
    dispatch(
      savePricingDefaults({
        // radius from LOCAL state; location echoed
        serviceRadiusKm: radius ? Number(radius) : null,
        baseLatitude: settings?.baseLatitude ?? null,
        baseLongitude: settings?.baseLongitude ?? null,
        // ── ~18 Pricing-owned passthrough echoed from `settings` (§2 — omit ⇒
        //    the Pricing page's values get WIPED; reciprocal of SP7's echo) ──
        hourlyRate: settings?.hourlyRate ?? null,
        travelRatePerKm: settings?.travelRatePerKm ?? null,
        travelRatePerHour: settings?.travelRatePerHour ?? null,
        warrantyPeriodCode: settings?.warrantyPeriodCode ?? null,
        customWarrantyPeriod: settings?.customWarrantyPeriod ?? null,
        paymentTermsCode: settings?.paymentTermsCode ?? null,
        customPaymentTerms: settings?.customPaymentTerms ?? null,
        validityPeriodCode: settings?.validityPeriodCode ?? null,
        customValidityPeriod: settings?.customValidityPeriod ?? null,
        includeDiagnostics: settings?.includeDiagnostics ?? false,
        includeLaborForRepair: settings?.includeLaborForRepair ?? false,
        includePartsAndMaterials: settings?.includePartsAndMaterials ?? false,
        includeTestingAndCalibration: settings?.includeTestingAndCalibration ?? false,
        includeWorkAreaCleanup: settings?.includeWorkAreaCleanup ?? false,
        includeDetailedServiceReport: settings?.includeDetailedServiceReport ?? false,
        // ── 12 business-hours keys from LOCAL state (legacy conditionals) ──
        orderDeadlineEnabled: deadlineEnabled,
        orderDeadlineTime: deadlineEnabled ? toTimeSpan(deadlineTime) : null,
        orderDeadlineTimezoneId: deadlineEnabled ? detectedTimezone : null,
        usePerDayDeadlines: deadlineEnabled ? usePerDay : false,
        mondayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.monday) : null,
        tuesdayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.tuesday) : null,
        wednesdayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.wednesday) : null,
        thursdayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.thursday) : null,
        fridayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.friday) : null,
        saturdayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.saturday) : null,
        sundayDeadlineTime: deadlineEnabled && usePerDay ? toTimeSpan(perDayTimes.sunday) : null,
      })
    ).then((result) => {
      if (savePricingDefaults.fulfilled.match(result)) {
        // Sync loaded* snapshots from LOCAL state — parity, no refetch (§2).
        setLoadedRadius(radius);
        setLoadedDeadlineEnabled(deadlineEnabled);
        setLoadedDeadlineTime(deadlineTime);
        setLoadedUsePerDay(usePerDay);
        setLoadedPerDayTimes({ ...perDayTimes });
      }
    });
  };

  const handleDayTimeChange = (day: string, value: string) => {
    setPerDayTimes((prev) => ({ ...prev, [day]: value }));
  };

  return (
    // Page root: NO animation (cards animate individually — a root transform
    // would trap the fixed-position ActionToast). Parity with PricingPageV2.
    <div style={{ display: "flex", flexDirection: "column", gap: 11, minWidth: 0, flex: 1 }}>
      <ActionToast
        open={!!success}
        variant="success"
        message={t("settings:service.saveSuccess")}
        onClose={() => dispatch(clearPricingSuccess())}
        duration={6000}
      />
      <ActionToast
        open={!!error}
        variant="error"
        message={t("settings:service.saveError")}
        onClose={() => dispatch(clearPricingError())}
        duration={6000}
      />

      {/* Card 1 — Nastavenia služby (the ONE Save persists BOTH cards) */}
      <SCard
        title={t("settings:service.title")}
        hint={t("settings:service.radiusDescription")}
        action={
          <SSaveBtn
            enabled={isDirty && !saving}
            busy={saving}
            label={saving ? t("settings:service.saving") : tCommon("buttons.save")}
            onClick={handleSave}
          />
        }
        style={cardAnim(0)}
      >
        <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
          <div style={{ width: 140 }}>
            <SInputField
              label={t("settings:service.radiusLabel")}
              type="number"
              mono
              value={radius}
              onChange={setRadius}
              unit={t("settings:service.radiusRange")}
              inputProps={{ min: 0, max: 5000 }}
            />
          </div>
          <span style={{ fontSize: 13, color: "var(--muted)", paddingBottom: 22 }}>
            {t("settings:service.radiusUnit")}
          </span>
        </div>

        {/* Kategórie služieb row — the i18n fix: SComingPill replaces the legacy
            hardcoded English "Coming soon". mapPreview/availability stay dead. */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginTop: 14,
            paddingTop: 12,
            borderTop: "1px solid var(--border-soft)",
          }}
        >
          <div style={{ flex: 1, fontSize: 13, color: "var(--ink-2)" }}>
            {t("settings:service.categories")}
          </div>
          <SComingPill label={t("settings:service.comingSoon")} />
        </div>
      </SCard>

      {/* Card 2 — Otváracie hodiny */}
      <ServiceBusinessHoursCardV2
        deadlineEnabled={deadlineEnabled}
        deadlineTime={deadlineTime}
        usePerDay={usePerDay}
        perDayTimes={perDayTimes}
        detectedTimezone={detectedTimezone}
        onToggleDeadline={setDeadlineEnabled}
        onChangeTime={setDeadlineTime}
        onTogglePerDay={setUsePerDay}
        onChangeDayTime={handleDayTimeChange}
        isPhone={isPhone}
        animationDelay="0.12s"
      />
    </div>
  );
};

export default ServicePageV2;
